import { useCallback, useState } from "react";
import { useDispatchApp } from "../lib/redux";
import { startResizeImage, startAddContentsToList } from "../actions/user";
import { startPersistCreativeJobResults } from "../actions/creativeJobs";
import { getIsBlockedActions } from "../actions/getters";
import { useCreditActionCosts } from "./useCreditActionCosts";
import { buildResizeImagePayload } from "../utils/buildResizeImagePayload";
import { extractResizeResultUrls } from "../utils/creativeJobResultUrls";

/**
 * Resize a single image and persist it to the content library.
 */
export const useResizeImageToLibrary = () => {
  const [isResizing, setIsResizing] = useState(false);
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION } = useCreditActionCosts();

  const resizeImageToLibrary = useCallback(
    async ({ sourceUrl, format, width, height }) => {
      if (!sourceUrl) {
        return { success: false, cancelled: true };
      }

      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION,
          multiplier: 1,
          showUsageConfirmationAlert: true,
        })
      );
      if (isBlocked) {
        return { success: false, cancelled: true };
      }

      setIsResizing(true);
      try {
        const response = await dispatch(
          startResizeImage(
            buildResizeImagePayload({
              urls: [sourceUrl],
              format,
              width,
              height,
            })
          )
        );

        if (!response?.ok) {
          return { success: false };
        }

        const resizedUrls = extractResizeResultUrls(response);
        const newUrl = resizedUrls[0];

        if (!newUrl) {
          return { success: false };
        }

        const responseSavedImage = response?.jobID
          ? await dispatch(
              startPersistCreativeJobResults({
                jobID: response.jobID,
                contents: [
                  {
                    url: newUrl,
                    from: "upload",
                    userID: null,
                  },
                ],
              })
            )
          : await dispatch(
              startAddContentsToList([
                {
                  url: newUrl,
                  from: "upload",
                  userID: null,
                },
              ])
            );

        const persistedUrl = response?.jobID
          ? responseSavedImage?.data
            ? Object.values(responseSavedImage.data)[0]?.url || newUrl
            : newUrl
          : responseSavedImage
            ? Object.values(responseSavedImage)[0]?.url || newUrl
            : newUrl;

        return { success: true, url: persistedUrl };
      } finally {
        setIsResizing(false);
      }
    },
    [dispatch, ADS_CAMPAIGN_GENERATE_CREATIVES_ACTION]
  );

  return { resizeImageToLibrary, isResizing };
};
